import DialogTextField from '@Components/General/dialog-content/text-field';
import useDialog from '@hooks/useDialog';
import {
    CancelScheduleSendRounded,
    ContentCopyRounded,
    DeleteOutlineRounded,
    EventRepeatRounded,
} from '@mui/icons-material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from '@tanstack/react-router';
import { closeOptions } from '@utils/utils';
import { createElement } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

export const useSchesuledEmailOptions = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { scheduledId } = useParams({});

    const dialog = useDialog();
    const queryClient = useQueryClient();

    const formHook = useForm();

    const refetch = () =>
        queryClient.invalidateQueries({ queryKey: ['emailsScheduled'] });

    const deleteMutation = useMutation({
        mutationKey: ['deleteScheduledEmail'],
        onSuccess: () => {
            dialog.close();
            closeOptions();
            refetch();
            if (scheduledId) {
                navigate({ to: '/emails/scheduled' });
            }
        },
    });

    const unscheduleMutation = useMutation({
        mutationKey: ['unscheduleEmail'],
        onSuccess: () => {
            refetch();
            closeOptions();
        },
    });

    const rescheduleMutation = useMutation({
        mutationKey: ['scheduleEmailDraft'],
        onSuccess: () => {
            refetch();
            dialog.close();
            closeOptions();
        },
    });

    const duplicateMutation = useMutation({
        mutationKey: ['duplicateScheduledEmail'],
        onSuccess: res => {
            navigate({ to: `/emails/drafts/${res.id}` });
        },
    });

    const getOptions = item => ({
        reschedule: {
            label: t('Reschedule'),
            onClick: () =>
                dialog.open({
                    title: t('Reschedule Email'),
                    content: createElement(DialogTextField, {
                        label: t('Schedule to'),
                        type: 'datetime-local',
                        control: formHook.control,
                        name: 'schedule_time',
                        defaultValue: item.schedule_time.toLocalInputDateTime(),
                        inputProps: {
                            min: new Date().toString().toLocalInputDateTime(),
                        },
                    }),
                    confirmLabel: t('Reschedule'),
                    onConfirm: () => [item.id, formHook.control._formValues],
                    confirmMutation: rescheduleMutation,
                }),
            Icon: EventRepeatRounded,
        },
        unschedule: {
            label: t('Unschedule'),
            onClick: () => unscheduleMutation.mutate(item.id),
            Icon: CancelScheduleSendRounded,
        },
        deleteEmail: {
            label: t('Delete'),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
            onClick: () =>
                dialog.open({
                    confirmLabel: t('Delete'),
                    title: t('Delete Scheduled Email') + '?',
                    message: createElement(Trans, {
                        t,
                        values: { subject: item.subject },
                        children: 'prompts:content.deleteScheduledEmail',
                    }),
                    onConfirm: () => item.id,
                    confirmMutation: deleteMutation,
                    confirmButtonProps: {
                        color: 'error',
                    },
                }),
        },
        duplicate: {
            label: t('Make a Copy'),
            onClick: () => duplicateMutation.mutate(item.id),
            Icon: ContentCopyRounded,
        },
        unscheduleMutation,
    });

    return getOptions;
};
